export function NegotiationTactics() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Negotiation Tactics for Apartment Building Sales
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Skilled negotiation is among the most valuable tips for selling an apartment building at the best 
            possible price and terms. Understanding buyer motivations, leverage points, and negotiation strategies 
            can add hundreds of thousands of dollars to your final sale price while ensuring favorable terms.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/d72bfa435db0ee10856cab8bc12a278f.jpg"
              alt="Real estate negotiation"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Buyer Motivations</h3>
            <p className="text-gray-600 mb-6">
              Different types of buyers have different priorities and motivations. Recognizing what drives each 
              buyer type allows you to emphasize the aspects of your property that matter most to them and 
              structure deals that meet their needs while maximizing your returns. This understanding is crucial 
              for effective negotiation.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Individual Investors</h4>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Priorities:</strong> Cash flow, appreciation potential, tax benefits
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Negotiation Approach:</strong> Emphasize stable income, low maintenance, and value-add 
                  opportunities. These buyers often have more flexibility on terms but may need financing contingencies.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2">Institutional Buyers</h4>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Priorities:</strong> Market position, portfolio fit, predictable returns
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Negotiation Approach:</strong> Highlight professional management, strong financials, 
                  and market fundamentals. These buyers move quickly but have strict investment criteria.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2">Value-Add Investors</h4>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Priorities:</strong> Below-market rents, renovation opportunities, operational improvements
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Negotiation Approach:</strong> Document upside potential and provide detailed proformas. 
                  These buyers may pay premium prices if they see significant value-add opportunities.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2">1031 Exchange Buyers</h4>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Priorities:</strong> Timeline certainty, smooth closing, similar or greater value
                </p>
                <p className="text-gray-600 text-sm">
                  <strong>Negotiation Approach:</strong> Offer certainty and flexibility on closing dates. These 
                  buyers are often motivated and may pay premiums to meet exchange deadlines.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Key Negotiation Points Beyond Price
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            While price is important, experienced sellers know that other terms can be equally valuable. These 
            negotiation points can make the difference between a good deal and a great one, and understanding 
            them is essential among tips for selling an apartment building successfully.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-calendar-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Closing Timeline</h4>
                  <p className="text-gray-600 text-sm">
                    Negotiate closing dates that work for your situation. If you need time to relocate or complete 
                    a 1031 exchange, build that into the contract. Conversely, if a buyer needs a quick close, 
                    you can often command a premium price for accommodating their timeline.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-orange-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Earnest Money Deposit</h4>
                  <p className="text-gray-600 text-sm">
                    Require substantial earnest money deposits (typically 1-3% of purchase price) to ensure buyer 
                    commitment. Larger deposits indicate serious buyers and provide protection if the deal falls 
                    through. Negotiate non-refundable deposits after due diligence for additional security.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-search-line text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Due Diligence Period</h4>
                  <p className="text-gray-600 text-sm">
                    Limit due diligence periods to 30-45 days for most transactions. Longer periods tie up your 
                    property and create uncertainty. If buyers request extensions, consider requiring additional 
                    non-refundable deposits or price increases to compensate for the extended timeline.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Contingencies</h4>
                  <p className="text-gray-600 text-sm">
                    Minimize contingencies whenever possible. Financing contingencies, inspection contingencies, 
                    and sale of other property contingencies all create risk. Cash offers or buyers with proof 
                    of funds are more valuable even at slightly lower prices due to certainty of closing.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-tools-line text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Repair Credits and Concessions</h4>
                  <p className="text-gray-600 text-sm">
                    Be strategic about repair credits. Sometimes it's better to complete repairs yourself to 
                    control costs and quality. Other times, offering credits is more efficient. Negotiate caps 
                    on repair obligations and avoid open-ended commitments to fix all inspection items.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-hand-coin-line text-amber-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Seller Financing</h4>
                  <p className="text-gray-600 text-sm">
                    Consider offering seller financing to attract more buyers and potentially command higher 
                    prices. This can be especially effective in tight lending markets. Ensure proper security, 
                    adequate down payments, and favorable interest rates that compensate for the risk.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Handling Multiple Offers</h3>
            <p className="text-gray-600 mb-6">
              Receiving multiple offers is the ideal scenario when selling an apartment building. However, 
              managing multiple offers requires strategy and finesse to maximize your outcome. These tips for 
              selling an apartment building in competitive situations can help you leverage multiple offers 
              into the best possible deal.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-auction-line text-orange-500 mr-2"></i>
                  Create Competition
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  When you receive multiple offers, let buyers know (without revealing specific terms) that 
                  you have competing interest. Set a deadline for best and final offers to create urgency 
                  and encourage buyers to submit their strongest terms.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-scales-line text-green-500 mr-2"></i>
                  Evaluate Total Package
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  Don't automatically accept the highest price. Consider the entire offer including contingencies, 
                  closing timeline, earnest money, buyer qualifications, and likelihood of closing. A slightly 
                  lower all-cash offer with no contingencies may be more valuable than a higher financed offer.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-chat-3-line text-red-500 mr-2"></i>
                  Negotiate with Top Candidates
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  After reviewing all offers, select your top 2-3 candidates and negotiate with each to improve 
                  terms. You can use competing offers as leverage to push for better prices, shorter contingency 
                  periods, or more favorable terms without revealing specific details from other offers.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-shield-star-line text-indigo-500 mr-2"></i>
                  Keep Backup Offers
                </h4>
                <p className="text-gray-600 text-sm">
                  Even after accepting an offer, keep backup offers in position until you close. If your primary 
                  buyer fails to perform or backs out during due diligence, having backup offers ready prevents 
                  starting over and maintains your negotiating position.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=multiple%20real%20estate%20investors%20competing%20for%20apartment%20building%20property%20auction%20bidding%20war%20commercial%20real%20estate%20deal%20negotiation%20business%20people%20reviewing%20offers%20and%20contracts%20competitive%20market&width=800&height=600&seq=tips-negotiation-002&orientation=landscape"
              alt="Multiple offer negotiation"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}